import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { FinanceWorkspaceShell } from './FinanceWorkspaceShell';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

type AccountingPeriod = {
  id: string;
  period_name: string;
  start_date: string;
  end_date: string;
  fiscal_year: number;
  status: string;
  closed_at: string | null;
  closed_by: string | null;
};

type CloseCockpitData = {
  periods: AccountingPeriod[];
  selectedPeriod: AccountingPeriod | null;
  cockpit: {
    period: AccountingPeriod;
    readiness: {
      checks: {
        unpostedEntries: number;
        unbalancedEntries: number;
        openAuditFindings: number;
        criticalAuditFindings: number;
        unreconciledStatements: number;
        unmatchedStatementLines: number;
      };
      blockerCount: number;
      blockerSummary: string[];
      canSoftClose: boolean;
    };
    bank: {
      unreconciledStatements: number;
      unmatchedStatementLines: number;
      inProgressReconciliations: number;
      completedReconciliations: number;
      approvedReconciliations: number;
    };
    audit: {
      openFindings: number;
      criticalFindings: number;
      taxFindings: number;
      findings: Array<{
        id: string;
        check_area: string;
        severity: string;
        status: string;
        title: string;
        entity_type: string | null;
        entity_id: string | null;
        detected_at: string;
      }>;
    };
    assets: {
      activeAssets: number;
      pendingDepreciationSchedules: number;
      postedDepreciationThisPeriod: number;
    };
    reports: {
      lockedSnapshots: number;
    };
    blockers: Array<{
      code: string;
      label: string;
      count: number;
      href: string;
    }>;
  } | null;
};

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

function StatusBadge({ status }: { status: string }) {
  const normalized = String(status || '').toLowerCase();
  const className =
    normalized === 'open'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : normalized === 'soft_closed'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <Badge variant="outline" className={className}>
      {status.toUpperCase()}
    </Badge>
  );
}

export default function CloseCockpitPage() {
  const [data, setData] = useState<CloseCockpitData | null>(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [runningDepreciation, setRunningDepreciation] = useState(false);
  const [progressingClose, setProgressingClose] = useState(false);

  const fetchData = useCallback(async (periodId?: string) => {
    setLoading(true);
    try {
      const suffix = periodId ? `?periodId=${encodeURIComponent(periodId)}` : '';
      const res = await api.get<CloseCockpitData>(`/finance/close-cockpit${suffix}`);
      setData(res ?? null);
      const resolvedId = res?.selectedPeriod?.id ?? '';
      setSelectedPeriodId(resolvedId);
    } catch {
      setData(null);
      setSelectedPeriodId('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const cockpit = data?.cockpit ?? null;
  const selectedPeriod = data?.selectedPeriod ?? null;
  const checklistItems = useMemo(() => {
    if (!cockpit) return ['No accounting period selected yet.'];
    if (cockpit.readiness.blockerSummary.length === 0) {
      return ['Current period is ready for soft close based on posting, audit, and bank checks.'];
    }
    return cockpit.readiness.blockerSummary;
  }, [cockpit]);

  const handleRunAudit = useCallback(async () => {
    if (!selectedPeriod) return;
    setRunningAudit(true);
    try {
      await api.post(`/finance/audit/run-checks?from=${encodeURIComponent(selectedPeriod.start_date)}&to=${encodeURIComponent(selectedPeriod.end_date)}`, {});
      toast.success('Process audit checks completed');
      await fetchData(selectedPeriod.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to run audit checks');
    } finally {
      setRunningAudit(false);
    }
  }, [fetchData, selectedPeriod]);

  const handleRunDepreciation = useCallback(async () => {
    if (!selectedPeriod) return;
    const end = new Date(selectedPeriod.end_date);
    const periodYear = end.getFullYear();
    const periodMonth = end.getMonth() + 1;
    setRunningDepreciation(true);
    try {
      const res = await api.post<{ postedCount: number; postedAmount: number }>('/finance/fixed-assets/depreciation/run', {
        periodYear,
        periodMonth,
      });
      toast.success(`Depreciation run completed: ${res.postedCount} schedule(s)`);
      await fetchData(selectedPeriod.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to run depreciation');
    } finally {
      setRunningDepreciation(false);
    }
  }, [fetchData, selectedPeriod]);

  const handleProgressClose = useCallback(async () => {
    if (!selectedPeriod || !cockpit) return;
    setProgressingClose(true);
    try {
      if (selectedPeriod.status === 'open') {
        let force = false;
        if (cockpit.readiness.blockerCount > 0) {
          const confirmed = window.confirm('This period still has blockers. Force soft close anyway?');
          if (!confirmed) {
            setProgressingClose(false);
            return;
          }
          force = true;
        }
        await api.post('/accounting-periods/month-end-close', {
          periodId: selectedPeriod.id,
          force,
        });
        toast.success(force ? 'Period force soft-closed' : 'Period soft-closed');
      } else if (selectedPeriod.status === 'soft_closed') {
        await api.patch(`/accounting-periods/${selectedPeriod.id}`, { status: 'closed' });
        toast.success('Period closed');
      }
      await fetchData(selectedPeriod.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to progress period close');
    } finally {
      setProgressingClose(false);
    }
  }, [cockpit, fetchData, selectedPeriod]);

  return (
    <FinanceWorkspaceShell
      title="Close Cockpit"
      subtitle="Period readiness · bank reconciliation · audit findings · asset posting"
      Icon={Lock}
      iconClassName="text-amber-600"
      headerActions={
        <>
          <Select value={selectedPeriodId} onValueChange={(value) => void fetchData(value)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select accounting period" />
            </SelectTrigger>
            <SelectContent>
              {(data?.periods ?? []).map((period) => (
                <SelectItem key={period.id} value={period.id}>
                  {period.period_name} · {period.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => void fetchData(selectedPeriodId || undefined)} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => void handleRunAudit()} disabled={loading || runningAudit || !selectedPeriod}>
            <RefreshCw className={`mr-2 h-4 w-4 ${runningAudit ? 'animate-spin' : ''}`} />
            Run Audit Checks
          </Button>
          <Button size="sm" variant="outline" onClick={() => void handleRunDepreciation()} disabled={loading || runningDepreciation || !selectedPeriod}>
            <RefreshCw className={`mr-2 h-4 w-4 ${runningDepreciation ? 'animate-spin' : ''}`} />
            Run Depreciation
          </Button>
          <Button size="sm" onClick={() => void handleProgressClose()} disabled={loading || progressingClose || !selectedPeriod || selectedPeriod.status === 'closed' || selectedPeriod.status === 'year_end'}>
            <Lock className={`mr-2 h-4 w-4 ${progressingClose ? 'animate-pulse' : ''}`} />
            {selectedPeriod?.status === 'soft_closed' ? 'Close Period' : 'Soft Close'}
          </Button>
        </>
      }
      moduleBadge="AC operating layer"
      domainBadge="Month-end close"
      agentTitle="One close surface for period blockers"
      agentSummary="Use one screen to see posting blockers, bank reconciliation exceptions, open audit findings, and pending depreciation before period close drifts across multiple finance pages."
      checklistTitle="Close Repair Checklist"
      checklistStatus={cockpit?.readiness.canSoftClose ? 'Ready for soft close' : 'Blockers need attention'}
      checklistNote="This page is the period close control surface. It does not replace ledger truth; it points operators to the exact workflow that still blocks close."
      checklistItems={checklistItems}
      quickActions={[
        { label: 'Accounting Periods', to: '/finance/accounting-periods' },
        { label: 'Reconciliation', to: '/finance/reconciliation' },
        { label: 'Bank Recon Pack', to: '/finance/reconciliation-pack' },
        { label: 'Process Audit', to: '/finance/reports/process-audit' },
        { label: 'Tax Report', to: '/finance/reports/tax' },
        { label: 'Fixed Assets', to: '/finance/fixed-assets' },
        { label: 'Audit Report', to: '/finance/reports/audit-report', variant: 'secondary' },
      ]}
    >
      {loading ? (
        <Card><CardContent className="p-8 text-sm text-muted-foreground">Loading close cockpit…</CardContent></Card>
      ) : !cockpit || !selectedPeriod ? (
        <Card><CardContent className="p-8 text-sm text-muted-foreground">No accounting period available. Create one in Accounting Periods first.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Period</div><div className="mt-1 text-base font-semibold">{selectedPeriod.period_name}</div><div className="mt-1"><StatusBadge status={selectedPeriod.status} /></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Close Blockers</div><div className={`mt-1 text-2xl font-semibold ${cockpit.readiness.blockerCount > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{cockpit.readiness.blockerCount}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unmatched Bank Lines</div><div className="mt-1 text-2xl font-semibold">{cockpit.bank.unmatchedStatementLines}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Open Audit Findings</div><div className="mt-1 text-2xl font-semibold">{cockpit.audit.openFindings}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Tax Readiness Findings</div><div className="mt-1 text-2xl font-semibold">{cockpit.audit.taxFindings}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Pending Depreciation</div><div className="mt-1 text-2xl font-semibold">{cockpit.assets.pendingDepreciationSchedules}</div></CardContent></Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
            <Card>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Blocker Queue</div>
                    <div className="text-xs text-muted-foreground">Each blocker opens the workspace that still needs work before soft close.</div>
                  </div>
                  {cockpit.readiness.canSoftClose ? (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Ready
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      Action needed
                    </Badge>
                  )}
                </div>
                {cockpit.blockers.length === 0 ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3 text-sm text-emerald-800">
                    No period blockers detected. You can move into soft close review.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cockpit.blockers.map((blocker) => (
                      <Link
                        key={blocker.code}
                        to={blocker.href}
                        className="flex items-center justify-between rounded-xl border bg-white px-3 py-3 text-sm hover:bg-muted/40"
                      >
                        <div>
                          <div className="font-medium text-slate-900">{blocker.label}</div>
                          <div className="text-xs text-muted-foreground">{blocker.code}</div>
                        </div>
                        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                          {blocker.count}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-4">
                <div>
                  <div className="text-sm font-semibold">Close Status</div>
                  <div className="text-xs text-muted-foreground">Period {fmtDate(selectedPeriod.start_date)} to {fmtDate(selectedPeriod.end_date)}</div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border bg-white px-3 py-2">
                    <div className="text-xs text-muted-foreground">Bank reconciliation</div>
                    <div className="mt-1 text-sm font-semibold">{cockpit.bank.inProgressReconciliations} in progress</div>
                    <div className="mt-1 text-xs text-muted-foreground">{cockpit.bank.completedReconciliations} completed · {cockpit.bank.approvedReconciliations} approved</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-2">
                    <div className="text-xs text-muted-foreground">Asset posting</div>
                    <div className="mt-1 text-sm font-semibold">{cockpit.assets.activeAssets} active assets</div>
                    <div className="mt-1 text-xs text-muted-foreground">{cockpit.assets.postedDepreciationThisPeriod} depreciation runs posted this period</div>
                  </div>
                </div>
                <div className="rounded-xl border bg-white px-3 py-2">
                  <div className="text-xs text-muted-foreground">Period audit trail</div>
                  <div className="mt-1 text-sm font-semibold">
                    {selectedPeriod.closed_at ? `Closed ${fmtDate(selectedPeriod.closed_at)}` : 'Not closed yet'}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {selectedPeriod.closed_by ? `Closed by ${selectedPeriod.closed_by}` : 'No close actor recorded'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <CardContent className="p-4">
                <div className="mb-3 text-sm font-semibold">Close Readiness Checks</div>
                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    ['Unposted entries', cockpit.readiness.checks.unpostedEntries],
                    ['Unbalanced entries', cockpit.readiness.checks.unbalancedEntries],
                    ['Open audit findings', cockpit.readiness.checks.openAuditFindings],
                    ['Critical findings', cockpit.readiness.checks.criticalAuditFindings],
                    ['Unreconciled statements', cockpit.readiness.checks.unreconciledStatements],
                    ['Unmatched bank lines', cockpit.readiness.checks.unmatchedStatementLines],
                  ].map(([label, value]) => (
                    <div key={String(label)} className="rounded-xl border bg-muted/20 px-3 py-2">
                      <div className="text-xs text-muted-foreground">{label}</div>
                      <div className="mt-1 text-lg font-semibold">{value}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="mb-3 text-sm font-semibold">Open Audit Findings</div>
                {cockpit.audit.findings.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No open findings in the selected period.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Area</TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cockpit.audit.findings.map((finding) => (
                          <TableRow key={finding.id}>
                            <TableCell className="font-mono text-xs">{finding.check_area}</TableCell>
                            <TableCell><Badge variant="outline">{finding.severity}</Badge></TableCell>
                            <TableCell>{finding.title}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fmtDate(finding.detected_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </FinanceWorkspaceShell>
  );
}
